import _ from "lodash";
import React from "react";
import {
  DeviceDesktopOutlined,
  LinkOutlined,
  GoogleDriveColored,
  OneDriveColored,
} from "@toddle-edu/ds-icons";

import {
  TEMPLATE_FIELD_LINES_OF_INQUIRY,
  TEMPLATE_FIELD_INQUIRY_QUESTIONS,
  TEMPLATE_FIELD_INQUIRY_QUESTIONS_V2,
} from "Constants/stringConstants";

export const MODAL_STATE = {
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  NULL: null,
};

export const INTERDISCIPLINARY_FIELD_MAPPING = {
  learningStandardMYP: "disciplinaryLearningStandardMYP",
  objectivesMYP: "disciplinaryObjectivesMYP",
  relatedConceptsMYP: "disciplinaryRelatedConceptsMYP",
  content: "disciplinaryContent",
};

export const ELEMENT_TYPE_ADD_LEARNING_GOALS = "AddLearningGoals";

// can't use them without selecting the unit
export const UNIT_DEPENDENT_LEARNING_GOALS = [
  TEMPLATE_FIELD_LINES_OF_INQUIRY,
  TEMPLATE_FIELD_INQUIRY_QUESTIONS,
  TEMPLATE_FIELD_INQUIRY_QUESTIONS_V2,
];

export const MYP_OBJECTIVES = ["objectivesMYP", "interdisciplinaryCriteriaMYP"];

export const METADATA_PROMPT_FIELDS = [
  "field",
  "type",
  "label",
  "config",
  "isRequired",
  "isDisabled",
];

export const SHOW_COUNT_FIELDS = [
  "atls",
  "benchmarks",
  "learningStandardUBD",
  "atlsUBD",
  "atlsMYP",
  "learningStandardMYP",
  "relatedConceptsMYP",
  "inquiryQuestions",
  "objectivesMYP",
  "interdisciplinaryCriteriaMYP",
  "atlDP",
  "subjectStandardDP",
  "syllabusContentDP",
];

// * required learning goals fields UIDs
export const REQUIRED_LEARNING_GOALS_FIELDS_UIDS = [
  "syllabusContentDP",
  "learningStandardUBD",
];

export const ALLOWED_FILE_TYPES = [
  "image/jpeg", // .jpeg
  "image/jpg", // .jpg
  "image/png", // .png
  "image/gif", // .gif,
  "image/webp", // .webp
  "text/plain", // .txt
  "text/csv", // .csv
  "application/pdf", // .pdf
  "application/vnd.google-apps.document", // Google Docs
  "application/vnd.google-apps.presentation", // Google Slides
  "application/vnd.google-apps.spreadsheet", // Google Sheets
  "application/msword", // .doc
  "application/vnd.ms-excel", // .xls
  "application/vnd.ms-powerpoint", // .ppt
  "application/vnd.ms-word.document.macroenabled.12", // .docm
  "application/vnd.ms-excel.sheet.macroenabled.12", // .xlsm
  "application/vnd.ms-powerpoint.presentation.macroenabled.12", // .pptm
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
];

export const ALLOWED_FILE_TYPES_V2 = [
  "image/*",
  "text/plain",
  "text/csv",
  "application/pdf",
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.presentation",
  "application/vnd.google-apps.spreadsheet",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.ms-word.document.macroenabled.12",
  "application/vnd.ms-excel.sheet.macroenabled.12",
  "application/vnd.ms-powerpoint.presentation.macroenabled.12",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/zip",
];

export const CREATE_DROPDOWN_OPTIONS = [
  {
    key: "upload_device",
    label: "common:upload_device",
    icon: <DeviceDesktopOutlined size={"xx-small"} />,
  },
  {
    key: "add_link",
    label: "common:add_link",
    icon: <LinkOutlined size={"xx-small"} />,
  },
  {
    key: "add_google_drive",
    label: "common:add_google_drive",
    icon: <GoogleDriveColored size={"xx-small"} />,
  },
  {
    key: "add_one_drive",
    label: "common:add_one_drive",
    icon: <OneDriveColored size={"xx-small"} />,
  },
];

export const MODAL_ERRORS = {
  FILE_SIZE: "FILE_SIZE",
  FILE_COUNT: "FILE_COUNT",
  ZERO_TOKENS: "ZERO_TOKENS",
};

export const DIALOGS_INFO = {
  [MODAL_ERRORS.FILE_COUNT]: {
    title: ({ t }) => t("ai:file_limit_exceeded"),
    message: ({ t, label }) =>
      t("ai:file_count_exceeded_msg", { count: label }),
    buttonTwoText: ({ t }) => t("common:okay"),
  },
  [MODAL_ERRORS.FILE_SIZE]: {
    title: ({ t }) => t("ai:file_limit_exceeded"),
    message: ({ t, label }) => {
      if (_.size(label) === 1) {
        return t("ai:file_limit_exceeded_msg", { label: _.first(label) });
      } else {
        return t("ai:file_limit_exceeded_plural_msg", {
          label: _.map(label, name => `‘${name}’`).join(", "),
        });
      }
    },
    buttonTwoText: ({ t }) => t("common:okay"),
  },
  [MODAL_ERRORS.ZERO_TOKENS]: {
    title: ({ t }) => t("ai:unable_to_access_the_shared_link"),
    message: ({ t, label }) => {
      return t("ai:toddle_ai_could_not_retrieve_content_from_label", {
        label,
      });
    },
    buttonTwoText: ({ t }) => t("common:okay"),
  },
};

export const INITIAL_DIALOG_STATE = {
  action: "",
  params: {},
};

export const MAX_SIZE_10_MEGA_BYTES = 10485760;

export const MAX_NUMBER_OF_FILES = 5;

export const TEMP_ID = "TEMP_";

export const ANIMATED_PLACEHOLDER_TEXTS = [
  "ai:exit_ticket_for_a_lesson_on_periodic_table_elements",
  "ai:practice_addition_of_fractions_with_unlike_denominators",
  "ai:walk_students_through_the_us_presidential_election_process",
  "ai:have_students_talk_about_their_dream_vacation_in_spanish",
  "ai:close_reading_of_macbeth_act_4_scene_3",
  "ai:check_student_understanding_about_the_causes_of_global_warming",
];

const MYP_LEARNING_GOALS_SORT_ORDER = [
  "mypObjectives",
  "interdisciplinaryCriteriaMYP",
  "atlsMYP",
  "conceptsMYP",
  "relatedConceptsMYP",
  "inquiryQuestions",
  "lpMYP",
  "learningStandardMYP",
];

const DP_LEARNING_GOALS_SORT_ORDER = [
  "syllabusContentDP",
  "atlDP",
  "learnerProfileDP",
  "subjectStandardDP",
];

export const CURRICULUM_SORT_ORDERS = {
  IB_DP: DP_LEARNING_GOALS_SORT_ORDER,
  IB_MYP: MYP_LEARNING_GOALS_SORT_ORDER,
};
